import { useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { AutoPricingCompRowOffsetModeSelector } from 'src/components/AutoPricing/AutoPricingCompRowOffsetModeSelector/AutoPricingCompRowOffsetModeSelector';
import {
  AutoPricingSettingsFormContent,
  AutoPricingSettingsFormContentCoreProps,
  PricingSetting,
} from 'src/components/AutoPricing/AutoPricingSettingsFormContent/AutoPricingSettingsFormContent';
import { useAutoPricingSettingsFormContext } from 'src/components/AutoPricing/useAutoPricingSettingsFormContext';
import { SeatScoreQuantityMultiplerGraph } from 'src/components/Events/RelativeScoreGraph/SeatScoreQuantityMultiplierGraph';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Switch } from 'src/core/ui';
import { useUserGroups } from 'src/hooks/useUserGroups';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { EventPricingSeatMapForm } from 'src/utils/eventWithDataUtils';
import { hasPermission } from 'src/utils/userPermissionUtils';
import {
  AutoPricingCompListingMode,
  EventAutoPricingSettingsInputs,
  Feature,
  Permission,
} from 'src/WebApiController';

type EventAutoPricingSettingsFormContentProps =
  AutoPricingSettingsFormContentCoreProps & {
    hasEventPricingTeamFeature?: boolean;
  };

export const EventAutoPricingSettingsFormContent = ({
  disabled,
  hasEventPricingTeamFeature,
  ...rest
}: EventAutoPricingSettingsFormContentProps) => {
  const hasCompRowOffsetModeFeature = useUserHasFeature(
    Feature.AutoPricingCompRowOffsetMode
  );

  const { watch: outerWatch, setValue: outerSetValue } =
    useFormContext<EventPricingSeatMapForm>();

  const { eventAutoPricing } = outerWatch();

  const innerMethods = useForm<EventAutoPricingSettingsInputs>({
    defaultValues: eventAutoPricing ?? undefined,
  });

  const { watch: innerWatch } = innerMethods;

  const { compListingMode } = innerWatch();

  innerWatch((eventAutoPricingNew) => {
    outerSetValue(
      'eventAutoPricing',
      eventAutoPricingNew as EventAutoPricingSettingsInputs
    );
    return;
  });

  const { userGroups, isLoading } = useUserGroups();
  const userGroupId = outerWatch('userGroupId');

  const { loginContext } = useAppContext();
  const canSetPrice = hasPermission(
    loginContext?.user,
    Permission.Inventory_SetPrice
  );

  const userGroupsContentSelect = useMemo(() => {
    const options = (userGroups ?? []).reduce<Record<string, string>>(
      (acc, userGroup) => {
        acc[userGroup.userGroupId.toString()] = userGroup.userGroupName;
        return acc;
      },
      {}
    );

    options[''] = 'None';

    return options;
  }, [userGroups]);

  return (
    <FormProvider {...innerMethods}>
      <>
        <AutoPricingSettingsFormContent
          isGlobalEdit={false}
          disabled={disabled}
          pricerTeamSectionChildren={
            hasEventPricingTeamFeature ? (
              <PricingSetting
                header={<Content id={ContentId.Team} />}
                detail={
                  <PosSelect
                    valueOptionsContent={userGroupsContentSelect}
                    loading={isLoading}
                    disabled={disabled || !canSetPrice}
                    value={userGroupId?.toString() ?? ''}
                    onChange={(newValue) => {
                      if (newValue === '') {
                        outerSetValue('userGroupId', null);
                      } else {
                        outerSetValue('userGroupId', parseInt(newValue));
                      }
                    }}
                  />
                }
              />
            ) : null
          }
          priceAgainstSectionChildren={
            <>
              {hasCompRowOffsetModeFeature && (
                <PricingSetting
                  header={<Content id={ContentId.MatchRowBy} />}
                  detail={
                    <AutoPricingCompRowOffsetModeSelector disabled={disabled} />
                  }
                />
              )}

              <AutoPricingFrequencyInput disabled={disabled} />
            </>
          }
          advancedSettingsSectionChildren={
            compListingMode == AutoPricingCompListingMode.QualityScore ? (
              <AdvancedSettingsSection disabled={disabled} />
            ) : null
          }
          hideCompListingQuantityFilters={true}
          hideBaseAdvancedSettingElements={true}
          {...rest}
        />
      </>
    </FormProvider>
  );
};

const AutoPricingFrequencyInput = ({ disabled }: { disabled: boolean }) => {
  const { setValue, watch } = useAutoPricingSettingsFormContext();

  const { autoPriceFrequency } = watch();

  return (
    <PricingSetting
      header={<Content id={ContentId.UpdatePriceEvery} />}
      detail={
        <PosTextField
          rootProps={{
            style: { maxWidth: '150px' },
          }}
          type="number"
          postfixDisplay={
            <FormatContent id={FormatContentId.NMinutes} params={''} />
          }
          value={autoPriceFrequency ?? ''}
          onChange={(e) => {
            let v = parseInt(e.target.value);
            if (!isNaN(v)) {
              if (v < 1) {
                v = 1;
              }
              setValue('autoPriceFrequency', v);
              return;
            } else {
              setValue('autoPriceFrequency', null);
            }
          }}
          disabled={disabled}
        />
      }
    />
  );
};

const AdvancedSettingsSection = ({ disabled }: { disabled: boolean }) => {
  const { setValue, watch } = useAutoPricingSettingsFormContext();

  const {
    compListingMode,
    compListingQuantityScoreAdjustmentEnabled,
    compListingQuantityScoreAdjustmentOverrideJson,
  } = watch();

  return (
    <>
      <PricingSetting
        header={<Content id={ContentId.CompListingQuantityScoreAdjustment} />}
        detail={
          <Switch
            disabled={disabled}
            checked={compListingQuantityScoreAdjustmentEnabled ?? false}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(isChecked) => {
              const compListingQuantityScoreAdjustmentEnabledNew = isChecked;
              if (
                compListingQuantityScoreAdjustmentEnabledNew !==
                compListingQuantityScoreAdjustmentEnabled
              ) {
                setValue(
                  'compListingQuantityScoreAdjustmentEnabled',
                  compListingQuantityScoreAdjustmentEnabledNew
                );
              }
            }}
          />
        }
      />

      <PricingSetting
        header={<Content id={ContentId.QuantityAvailablePremiums} />}
        detail={
          <SeatScoreQuantityMultiplerGraph
            initialSegments={
              compListingQuantityScoreAdjustmentOverrideJson
                ? JSON.parse(compListingQuantityScoreAdjustmentOverrideJson)
                    .segments
                : undefined
            }
            onChange={({ overrides, segments }) => {
              const compListingQuantityScoreAdjustmentOverrideJsonNew =
                JSON.stringify({ overrides, segments });
              setValue(
                'compListingQuantityScoreAdjustmentOverrideJson',
                compListingQuantityScoreAdjustmentOverrideJsonNew
              );
            }}
          />
        }
      />
    </>
  );
};
